import { useCallback, useEffect, useRef } from "react";

/**
 * Minimum meaningful horizontal overflow, in pixels. Below this threshold,
 * the element is treated as "no overflow" and the hook falls back to
 * default vertical scroll. Catches sub-pixel rounding and the scrollbar-
 * gutter artifacts that MUI DataGrid exhibits — without this, Ctrl+wheel
 * would "wiggle" the grid by 1-2 pixels and steal the user's vertical
 * scroll even when every column is fully visible.
 */
const MIN_OVERFLOW_PX = 8;

/**
 * While Ctrl/Cmd is held, translate vertical wheel deltas into horizontal
 * scroll on the element this hook is attached to. Convenience for users
 * without a horizontal-scroll input device (trackpad, tilt wheel) when a
 * table or grid overflows the viewport.
 *
 * No-ops when the element has no meaningful horizontal overflow, which is
 * important because `preventDefault()` would otherwise swallow the user's
 * vertical scroll and leave them wondering why nothing moved.
 *
 * Returns a callback ref — attach it to the scrollable element:
 *   const scrollRef = useCtrlWheelHorizontalScroll();
 *   <Box ref={scrollRef}>…</Box>
 *
 * The callback-ref form (rather than a passed-in RefObject) guarantees the
 * listener is attached the instant the DOM node mounts and detached the
 * instant it unmounts, regardless of conditional rendering.
 */
export function useCtrlWheelHorizontalScroll(): (el: HTMLElement | null) => void {
  const currentElRef = useRef<HTMLElement | null>(null);
  const currentHandlerRef = useRef<((e: WheelEvent) => void) | null>(null);

  const detach = useCallback(() => {
    const el = currentElRef.current;
    const handler = currentHandlerRef.current;
    if (el && handler) {
      el.removeEventListener("wheel", handler);
    }
    currentElRef.current = null;
    currentHandlerRef.current = null;
  }, []);

  useEffect(() => detach, [detach]);

  return useCallback(
    (el: HTMLElement | null) => {
      detach();
      if (!el) return;

      const handleWheel = (e: WheelEvent) => {
        if (!e.metaKey && !e.ctrlKey) return;
        if (e.deltaY === 0) return;
        if (el.scrollWidth - el.clientWidth <= MIN_OVERFLOW_PX) return;
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      };

      el.addEventListener("wheel", handleWheel, { passive: false });
      currentElRef.current = el;
      currentHandlerRef.current = handleWheel;
    },
    [detach]
  );
}
